import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["dialog", "input", "list"]

  static values = {
    commands: { type: Array, default: [] },
    context: { type: Object, default: {} }
  }

  connect() {
    this.selectedIndex = 0
    this.allResults = []
    this.dataLoaded = false
    this.recipes = []
    this.tasks = []
    this.groceryLists = []
    this.ingredients = []
    document.addEventListener("keydown", this.handleGlobalKeydown)
  }

  disconnect() {
    document.removeEventListener("keydown", this.handleGlobalKeydown)
  }

  async loadData() {
    if (this.dataLoaded) return

    try {
      const response = await fetch("/command_palette", {
        headers: { "Accept": "application/json" }
      })
      if (response.ok) {
        const data = await response.json()
        this.recipes = data.recipes || []
        this.tasks = data.tasks || []
        this.groceryLists = data.groceryLists || []
        this.ingredients = data.ingredients || []
        this.dataLoaded = true
        this.filter()
      }
    } catch (error) {
      console.error("Failed to load command palette data:", error)
    }
  }

  handleGlobalKeydown = (event) => {
    if ((event.metaKey || event.ctrlKey) && event.key === "k") {
      event.preventDefault()
      this.open()
    }
  }

  open() {
    this.dialogTarget.classList.remove("hidden")
    this.inputTarget.value = ""
    this.selectedIndex = 0
    this.filter()
    this.loadData()
    setTimeout(() => this.inputTarget.focus(), 10)
    document.body.style.overflow = "hidden"
  }

  close() {
    this.dialogTarget.classList.add("hidden")
    document.body.style.overflow = ""
  }

  closeOnBackdrop(event) {
    if (event.target === event.currentTarget) {
      this.close()
    }
  }

  get contextualCommands() {
    const ctx = this.contextValue
    const commands = []

    if (ctx.controller === "recipes" && ctx.action === "show" && ctx.id) {
      commands.push(
        { group: "Esta receta", label: "Editar receta", url: `/recipes/${ctx.id}/edit` },
        { group: "Esta receta", label: "Archivar receta", action: "archiveRecipe" }
      )
    }

    if (ctx.controller === "meal_plans") {
      commands.push(
        { group: "Meal Plan", label: "Ir a hoy", action: "scrollToToday" },
        { group: "Meal Plan", label: "Generar lista de compras", url: "/grocery_lists/generate" }
      )
    }

    if (ctx.controller === "grocery_lists" && ctx.action === "show" && ctx.id) {
      commands.push(
        { group: "Esta lista", label: "Marcar todo", action: "checkAllItems" },
        { group: "Esta lista", label: "Desmarcar todo", action: "uncheckAllItems" }
      )
    }

    if (ctx.controller === "tasks" && ctx.action === "show" && ctx.id) {
      commands.push(
        { group: "Esta tarea", label: "Marcar pendiente", action: "setTaskState", state: 0 },
        { group: "Esta tarea", label: "Marcar en progreso", action: "setTaskState", state: 1 },
        { group: "Esta tarea", label: "Marcar completada", action: "setTaskState", state: 2 },
        { group: "Esta tarea", label: "Marcar pausada", action: "setTaskState", state: 3 },
        { group: "Esta tarea", label: "Eliminar tarea", action: "deleteTask" }
      )
    }

    return commands
  }

  filter() {
    let query = this.normalize(this.inputTarget.value.toLowerCase().trim())
    let scope = null

    if (query.startsWith("r:") || query.startsWith("receta:")) {
      scope = "recipes"
      query = query.replace(/^(r:|receta:)/, "").trim()
    } else if (query.startsWith("t:") || query.startsWith("tarea:")) {
      scope = "tasks"
      query = query.replace(/^(t:|tarea:)/, "").trim()
    } else if (query.startsWith("l:") || query.startsWith("lista:")) {
      scope = "groceryLists"
      query = query.replace(/^(l:|lista:)/, "").trim()
    } else if (query.startsWith("i:") || query.startsWith("ingrediente:")) {
      scope = "ingredients"
      query = query.replace(/^(i:|ingrediente:)/, "").trim()
    }

    this.buildResults(query, scope)
    this.selectedIndex = 0
    this.render()
  }

  buildResults(query, scope) {
    this.allResults = []

    if (!scope) {
      const contextual = query
        ? this.contextualCommands.filter(cmd => this.normalize(cmd.label.toLowerCase()).includes(query))
        : this.contextualCommands

      contextual.forEach(cmd => {
        this.allResults.push({ ...cmd, type: "contextual" })
      })

      const filteredCommands = query
        ? this.commandsValue.filter(cmd =>
            this.normalize(cmd.label.toLowerCase()).includes(query) ||
            this.normalize(cmd.group.toLowerCase()).includes(query)
          )
        : this.commandsValue

      filteredCommands.forEach(cmd => {
        this.allResults.push({ ...cmd, type: "command" })
      })
    }

    if (!scope || scope === "recipes") {
      const filteredRecipes = query
        ? this.recipes.filter(r => this.normalize(r.name.toLowerCase()).includes(query))
        : (scope ? this.recipes : [])

      filteredRecipes.slice(0, 8).forEach(r => {
        this.allResults.push({
          ...r,
          type: "recipe",
          label: r.name,
          url: `/recipes/${r.id}`
        })
      })
    }

    if (!scope || scope === "tasks") {
      const filteredTasks = query
        ? this.tasks.filter(t => this.normalize(t.title.toLowerCase()).includes(query))
        : (scope ? this.tasks : [])

      filteredTasks.slice(0, 8).forEach(t => {
        this.allResults.push({
          ...t,
          type: "task",
          label: t.title,
          url: `/tasks/${t.id}`
        })
      })
    }

    if (!scope || scope === "groceryLists") {
      const filteredLists = query
        ? this.groceryLists.filter(l => this.normalize(l.name.toLowerCase()).includes(query))
        : (scope ? this.groceryLists : [])

      filteredLists.slice(0, 5).forEach(l => {
        this.allResults.push({
          ...l,
          type: "groceryList",
          label: l.name,
          url: `/grocery_lists/${l.id}`
        })
      })
    }

    if (!scope || scope === "ingredients") {
      const filteredIngredients = query
        ? this.ingredients.filter(i => this.normalize(i.name.toLowerCase()).includes(query))
        : (scope ? this.ingredients : [])

      filteredIngredients.slice(0, 8).forEach(i => {
        this.allResults.push({
          ...i,
          type: "ingredient",
          label: i.name,
          url: `/pantry?highlight=${i.id}`
        })
      })
    }
  }

  normalize(str) {
    return str.normalize("NFD").replace(/[\u0300-\u036f]/g, "")
  }

  navigate(event) {
    const count = this.allResults.length

    if (event.key === "ArrowDown") {
      event.preventDefault()
      this.selectedIndex = count > 0 ? (this.selectedIndex + 1) % count : 0
      this.render()
    } else if (event.key === "ArrowUp") {
      event.preventDefault()
      this.selectedIndex = count > 0 ? (this.selectedIndex - 1 + count) % count : 0
      this.render()
    } else if (event.key === "Enter") {
      event.preventDefault()
      this.selectCurrent()
    } else if (event.key === "Escape") {
      event.preventDefault()
      this.close()
    } else if (/^[0-9]$/.test(event.key) && !event.metaKey && !event.ctrlKey && !event.altKey) {
      const index = event.key === "0" ? 9 : parseInt(event.key) - 1
      if (index < count) {
        event.preventDefault()
        this.selectedIndex = index
        this.selectCurrent()
      }
    }
  }

  selectCurrent() {
    const item = this.allResults[this.selectedIndex]
    const query = this.inputTarget.value.trim()

    if (!item && query) {
      this.createTask(query)
      return
    }

    if (!item) return

    if (item.action) {
      this.executeAction(item)
    } else if (item.url) {
      this.close()
      Turbo.visit(item.url)
    }
  }

  async executeAction(item) {
    const ctx = this.contextValue

    switch (item.action) {
      case "archiveRecipe":
        this.close()
        await this.postAction(`/recipes/${ctx.id}/archive`, "PATCH")
        break

      case "scrollToToday":
        this.close()
        document.querySelector("[data-today]")?.scrollIntoView({ behavior: "smooth", block: "center" })
        break

      case "checkAllItems":
        this.close()
        await this.postAction(`/grocery_lists/${ctx.id}/check_all`, "PATCH")
        break

      case "uncheckAllItems":
        this.close()
        await this.postAction(`/grocery_lists/${ctx.id}/uncheck_all`, "PATCH")
        break

      case "setTaskState":
        this.close()
        await this.postAction(`/tasks/${ctx.id}`, "PATCH", `task[state]=${item.state}`)
        break

      case "deleteTask":
        if (confirm("¿Eliminar esta tarea?")) {
          this.close()
          await this.postAction(`/tasks/${ctx.id}`, "DELETE")
          Turbo.visit("/tasks")
        }
        break
    }
  }

  async postAction(url, method, body = "") {
    const csrfToken = document.querySelector("meta[name='csrf-token']")?.content

    try {
      const response = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          "Accept": "text/vnd.turbo-stream.html, text/html",
          "X-CSRF-Token": csrfToken
        },
        body
      })

      if (response.ok) {
        const contentType = response.headers.get("content-type")
        if (contentType?.includes("turbo-stream")) {
          const html = await response.text()
          Turbo.renderStreamMessage(html)
        } else if (response.redirected) {
          Turbo.visit(response.url)
        } else {
          location.reload()
        }
      }
    } catch (error) {
      console.error("Action failed:", error)
    }
  }

  async createTask(title) {
    const csrfToken = document.querySelector("meta[name='csrf-token']")?.content

    try {
      const response = await fetch("/tasks", {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          "Accept": "text/vnd.turbo-stream.html",
          "X-CSRF-Token": csrfToken
        },
        body: `task[title]=${encodeURIComponent(title)}`
      })

      if (response.ok) {
        const html = await response.text()
        Turbo.renderStreamMessage(html)
        this.close()
        window.showToast?.("Tarea creada")
      }
    } catch (error) {
      console.error("Error creating task:", error)
    }
  }

  selectAt(event) {
    const index = parseInt(event.currentTarget.dataset.index, 10)
    this.selectedIndex = index
    this.selectCurrent()
  }

  render() {
    const grouped = {}
    const groupOrder = ["Esta receta", "Esta tarea", "Esta lista", "Meal Plan", "Acciones", "Navegación", "Recetas", "Tareas", "Listas", "Ingredientes"]
    const typeToGroup = {
      command: null,
      contextual: null,
      recipe: "Recetas",
      task: "Tareas",
      groceryList: "Listas",
      ingredient: "Ingredientes"
    }

    this.allResults.forEach((item, idx) => {
      const group = (item.type === "command" || item.type === "contextual") ? item.group : typeToGroup[item.type]
      if (!grouped[group]) grouped[group] = []
      grouped[group].push({ ...item, globalIndex: idx })
    })

    let html = ""
    for (const group of groupOrder) {
      if (!grouped[group]) continue
      const items = grouped[group]

      html += `<div class="mb-2">
        <div class="px-3 py-1 text-[10px] font-mono uppercase tracking-wider text-cream/40">${group}</div>`

      for (const item of items) {
        const isSelected = item.globalIndex === this.selectedIndex
        const meta = this.renderMeta(item)
        const shortcut = item.globalIndex < 10
          ? `<kbd class="w-4 h-4 text-[10px] font-mono bg-cream/5 text-cream/30 border border-cream/10 flex items-center justify-center shrink-0">${item.globalIndex === 9 ? '0' : item.globalIndex + 1}</kbd>`
          : ''

        html += `
          <button type="button"
            data-index="${item.globalIndex}"
            data-action="click->command-palette#selectAt"
            class="w-full px-3 py-2 flex items-center gap-2 text-left transition-colors
              ${isSelected ? "bg-terracotta/20 border-l-2 border-terracotta text-cream" : "border-l-2 border-transparent text-cream/70 hover:bg-cream/5 hover:text-cream"}">
            ${shortcut}
            <span class="text-sm truncate flex-1">${item.label}</span>
            ${meta}
          </button>`
      }

      html += `</div>`
    }

    if (this.allResults.length === 0) {
      const query = this.inputTarget.value.trim()
      if (query) {
        html = `<div class="px-3 py-6 text-center text-cream/40 text-sm">
          <span class="text-cream/60">Enter</span> para crear tarea: "<span class="text-gold">${query}</span>"
        </div>`
      } else {
        html = `<div class="px-3 py-6 text-center text-cream/40 text-sm">Escribe para buscar</div>`
      }
    }

    this.listTarget.innerHTML = html
  }

  renderMeta(item) {
    if (item.type === "recipe") {
      const time = (item.prep_time_minutes || 0) + (item.cook_time_minutes || 0)
      const parts = []
      if (time) parts.push(`${time}min`)
      if (item.difficulty) parts.push(`<span class="text-${item.difficulty === 'easy' ? 'electric' : item.difficulty === 'medium' ? 'gold' : 'terracotta'}">${item.difficulty}</span>`)
      return parts.length ? `<span class="text-[10px] font-mono text-cream/30">${parts.join(" · ")}</span>` : ""
    }

    if (item.type === "task") {
      const states = { 0: "pendiente", 1: "en progreso", 2: "completada", 3: "pausada" }
      const colors = { 0: "cream/30", 1: "gold", 2: "electric", 3: "rose-dust" }
      return `<span class="text-[10px] font-mono text-${colors[item.state]}">${states[item.state] || ""}</span>`
    }

    if (item.type === "groceryList") {
      const statuses = { 0: "activa", 1: "completada" }
      return `<span class="text-[10px] font-mono text-cream/30">${statuses[item.status] || ""}</span>`
    }

    if (item.type === "ingredient") {
      const parts = []
      if (item.category_name) parts.push(item.category_name)
      if (item.default_unit) parts.push(item.default_unit)
      return parts.length ? `<span class="text-[10px] font-mono text-electric/50">${parts.join(" · ")}</span>` : ""
    }

    return ""
  }
}
